// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaProtestType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Col,
  Label,
  Row,
} from 'reactstrap';
import {
  AvField,
} from 'availity-reactstrap-validation';
import {
  InputCheckbox,
  InputDateTime,
  InputDrawing,
  InputFile,
  InputTypeahead,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  regattaProtestTypeLabel,
} from 'common.language';
import config from '../../../config';

// Types
import type {
  $ReducerState,
} from '../../../redux';

export type $OwnProps = $Translate & {
  getters: {
    getRegattaEntryOptions: (search: string) => Promise<Array<{ id: string; Name: string }>>;
  };
  item: $Model<'RegattaProtest'> | $ModelUpsertParams<'RegattaProtest'>;
  range: {
    max?: Date;
    min?: Date;
  };
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  showAll: boolean;
};

class RegattaProtestFieldsView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      showAll: false,
    };
  }

  render() {
    const {
      getters,
      item,
      main,
      range,
      t,
    } = this.props;
    const {
      showAll,
    } = this.state;

    return (
      <>
        <Row>
          <Col
            sm={2}
            xs={4}
          >
            <AvField
              label={t('Case no.')}
              name={'CaseNo'}
              required
              value={item.CaseNo}
            />
          </Col>
          <Col
            sm={3}
            xs={8}
          >
            <InputDateTime
              label={t('Hearing date')}
              labelOpenCalendar={t('Open calendar')}
              name={'HearingDate'}
              timezone={main.timezone}
              value={item.HearingDate}
            />
          </Col>
          <Col sm={7}>
            <AvField
              label={t('Decision')}
              maxLength={'255'}
              name={'Decision'}
              type={'text'}
              value={item.Decision}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col sm={6}>
            <Row>
              <Col xs={8}>
                <AvField
                  label={t('Race date')}
                  name={'RaceDate'}
                  range={range}
                  required
                  type={'date'}
                  value={item.RaceDate}
                />
              </Col>
              <Col xs={4}>
                <AvField
                  label={t('Race No.')}
                  name={'RaceNo'}
                  value={item.RaceNo}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <AvField
                  label={t('Type')}
                  name={'Type'}
                  required
                  type={'select'}
                  value={item.Type}
                >
                  {_.map(
                    RegattaProtestType,
                    (i) => (
                      <option
                        key={i}
                        value={i}
                      >
                        {t(regattaProtestTypeLabel[i])}
                      </option>
                    ),
                  )}
                </AvField>
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col xs={8}>
                <InputTypeahead
                  getOptions={(search) => getters.getRegattaEntryOptions(search)}
                  label={t('Protestor')}
                  name={'Protestor'}
                  required
                  value={item.Protestor}
                />
              </Col>
              <Col xs={4}>
                <AvField
                  label={t('Group')}
                  name={'ProtestorGroup'}
                  value={item.ProtestorGroup}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={8}>
                <InputTypeahead
                  getOptions={(search) => getters.getRegattaEntryOptions(search)}
                  label={t('Protestee')}
                  name={'Protestee'}
                  required
                  value={item.Protestee}
                />
              </Col>
              <Col xs={4}>
                <AvField
                  label={t('Group')}
                  name={'ProtesteeGroup'}
                  value={item.ProtesteeGroup}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {showAll && (
          <>
            <Row>
              <Col sm={6}>
                <InputDrawing
                  height={360}
                  label={t('Incident drawing')}
                  name={'Drawing'}
                  value={item.Drawing}
                  width={368}
                />
                <InputFile
                  fileDirUrl={`${config.CDN_URL}/images/max`}
                  imageThumbDirUrl={`${config.CDN_URL}/images/min`}
                  label={t('Incident drawing image')}
                  labelChangeFile={t('Change file')}
                  labelError={t('Error')}
                  labelFile={t('File')}
                  name={'Image'}
                  onError={ErrorLog.exception}
                  uploadUrl={`${config.CDN_URL}/v1.0/upload/image`}
                  value={item.Image}
                />
              </Col>
              <Col sm={6}>
                <Row>
                  <Col xs={8}>
                    <AvField
                      label={t('Witnesses')}
                      name={'Witnesses'}
                      value={item.Witnesses}
                    />
                  </Col>
                  <Col xs={4}>
                    <AvField
                      label={t('Rule broken')}
                      name={'Rule'}
                      value={item.Rule}
                    />
                  </Col>
                </Row>
                <AvField
                  label={t('Where and when the incident occurred')}
                  name={'PreContent'}
                  value={item.PreContent}
                />
                <AvField
                  label={t('Incident description')}
                  name={'Content'}
                  rows={10}
                  type={'textarea'}
                  value={item.Content}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Label>{t('Informing protestee')}</Label>
              </Col>
              <Col>
                <Label>{`${t('When')}? / ${t('Details')}`}</Label>
              </Col>
            </Row>
            <Row>
              <Col>
                <InputCheckbox
                  label={t('By hailing')}
                  name={'Hailing'}
                  value={item.Hailing}
                />
              </Col>
              <Col>
                <AvField
                  name={'HailingContent'}
                  value={item.HailingContent}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputCheckbox
                  label={t('By displaying a red flag')}
                  name={'Flag'}
                  value={item.Flag}
                />
              </Col>
              <Col>
                <AvField
                  name={'FlagContent'}
                  value={item.FlagContent}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <InputCheckbox
                  label={t('By informing her in some alternative way')}
                  name={'AltInfo'}
                  value={item.AltInfo}
                />
              </Col>
              <Col>
                <AvField
                  name={'AltInfoContent'}
                  value={item.AltInfoContent}
                />
              </Col>
            </Row>
          </>
        )}
        <Button
          className={'w-100 mt-1'}
          color={'secondary'}
          id={'regatta-protest-toggle-button'}
          onClick={() => this.setState({
            showAll: !showAll,
          })}
          size={'sm'}
        >
          {showAll ? t('Show less fields') : t('Show all fields')}
        </Button>
      </>
    );
  }
}

export default RegattaProtestFieldsView;
